require('./impositionSidebar.scss');

const PropTypes = require('prop-types');

// @ vendors
const React = require('react');
// @ helpers
const { FormattedText, formatText, getLanguage } = require('core/i18n').i18n;
const { toCamelCase } = require('utilities/stringHelper');
const { formatAmountObject, getPlainAmountObject } = require('utilities/currencyHelper');
const { getImpositionStatus, IMPOSITION_STATUS_VALID } = require('utilities/contactCenter/impositionsHelper');
// @ commons
const { OKSectionBox } = require('commonsComponents/OKSectionBox');
const OKInformativeList = require('components/rebrand/OKInformativeList/OKInformativeList');
const OKInformativeListItem = require('components/rebrand/OKInformativeListItem/OKInformativeListItem');
const { OneLineSymbolAmount } = require('commonsComponents/oneLineSymbolAmount');
const { LoadingSection } = require('commonsComponents/loadingSection');
const { OKLink } = require('commonsComponents/OKLink');
const ContactCenterRender = require('components/conditionalRender/contactCenterRender')
// @ constants
const { DEPOSIT_INTERVENTION_OWNER } = require('constants/index');
const language = getLanguage();

class ImpositionSidebar extends React.Component {
    getIntervenerAuthorized() {
        return this.props.immInterveners.filter(intervener => {
            return intervener.get('type') !== DEPOSIT_INTERVENTION_OWNER;
        });
    }

    getIntervenerHolders() {
        return this.props.immInterveners.filter(intervener => {
            return intervener.get('type') === DEPOSIT_INTERVENTION_OWNER;
        });
    }

    buildFundsAmountElement() {
        const { fund } = this.props;
        let fundsAmountElement;

        if (fund && fund.amountObject.amount) {
            const plainAmountObject = getPlainAmountObject(fund.amountObject);

            fundsAmountElement = (
                <OKInformativeListItem
                    className="imposition-sidebar__item"
                    title={formatText('imposition-fundsAmount')}
                >
                    <OneLineSymbolAmount
                        className="imposition-sidebar__amount"
                        amountObject={plainAmountObject}
                        language={language}
                        amountObjectFormatted={formatAmountObject(plainAmountObject, true)}
                    />
                </OKInformativeListItem>
            );
        }

        return fundsAmountElement;
    }

    buildFundsElement() {
        const { fund } = this.props;
        let fundElement;

        if (fund && fund.description) {
            fundElement = (
                <OKInformativeListItem
                    className="imposition-sidebar__item"
                    title={formatText('imposition-funds')}
                    bodyText={fund.description}
                />
            );
        }

        return fundElement;
    }

    buildInterneversContent() {
        let intervenerHoldersData = null;
        let intervenerAuthorizedData = null;
        const intervenerHolders = this.getIntervenerHolders();
        const invervenerAuthorized = this.getIntervenerAuthorized();

        if (intervenerHolders.size) {
            const intervenersSubtitle =
                intervenerHolders.size > 1
                    ? formatText('imposition-intervenerHolders')
                    : formatText('imposition-intervenerHolder');

            intervenerHoldersData = (
                <div className="imposition-sidebar__interveners--content">
                    <div className="imposition-sidebar__interveners--subtitle">
                        {intervenersSubtitle}
                    </div>
                    {intervenerHolders.map((intervener, index) => {
                        return (
                            <p key={index} className="imposition-sidebar__interveners--holder">
                                {intervener.get('name')}
                            </p>
                        );
                    })}
                </div>
            );
        }

        if (invervenerAuthorized.size) {
            const intervenersSubtitle =
                invervenerAuthorized.size > 1
                    ? formatText('imposition-intervenersAuthorized')
                    : formatText('imposition-intervenerAuthorized');

            intervenerAuthorizedData = (
                <div className="imposition-sidebar__interveners--content">
                    <div className="imposition-sidebar__interveners--subtitle">
                        {intervenersSubtitle}
                    </div>
                    {invervenerAuthorized.map((intervener, index) => {
                        return (
                            <p key={index} className="imposition-sidebar__interveners--holder">
                                {intervener.get('name')}
                            </p>
                        );
                    })}
                </div>
            );
        }

        const dataToRender = (
            <div>
                {intervenerHoldersData}
                {intervenerAuthorizedData}
            </div>
        );

        return dataToRender;
    }

    buildCCImpositionActionLinks() {
        const { immDeposits, depositId, impositionId, showImpositionCCActions } = this.props;

        // If it's not contact center instance or it's not required, then finish
        if (!(__CONTACT_CENTER__ && showImpositionCCActions)) return null;

        const immDeposit = immDeposits.getIn(['byId', depositId]);
        const immImposition = immDeposit.getIn(['impositionList', 'byId', impositionId]);

        // If the imposition is not active, then finish
        const impositionStatus = getImpositionStatus(immImposition, immDeposits.get('actualDate'));
        if (impositionStatus !== IMPOSITION_STATUS_VALID) return null;

        const isRenovationActive = immImposition.get('isRenovationActive');
        const cancelRenewalLinkTitleKey = isRenovationActive ? 'impositionInterestRenewal-cancelRenewalLinkTitle' : 'impositionInterestRenewal-setRenewalLinkTitle';
        const cancelRenewalLinkTextKey = isRenovationActive ? 'impositionInterestRenewal-cancelRenewalLinkText' : 'impositionInterestRenewal-setRenewalLinkText';
        const isRenewal = isRenovationActive ? 'N' : 'S';

        // Otherwise, return the link
        return (
            <div className="imposition-sidebar__items">
                <div className="imposition-sidebar__item imposition-sidebar__item--border">
                    <div className="imposition-sidebar__link-title">
                        <FormattedText value={cancelRenewalLinkTitleKey} format="firstUppercase" />
                    </div>
                    <OKLink
                        to={`/myprofile/deposits/${depositId}/imposition/${impositionId}/interest-renewal/${isRenewal}`}
                        modifier="inline"
                    >
                        <FormattedText value={cancelRenewalLinkTextKey} format="firstUppercase" />
                    </OKLink>
                </div>
            </div>
        );
    }

    render() {
        const {
            title,
            account,
            amountObject,
            isFetchingInterveners,
            contractNumber,
            showImpositionNumber,
            impositionId
        } = this.props;
        const plainAmountObject = getPlainAmountObject(amountObject);
        const fundsElement = this.buildFundsElement();
        const fundsAmountElement = this.buildFundsAmountElement();
        let intervenerElement;

        if (isFetchingInterveners) {
            intervenerElement = <LoadingSection />;
        } else {
            intervenerElement = this.buildInterneversContent();
        }

        return (
            <OKSectionBox>
                <h3 className="imposition-sidebar__title">
                    {toCamelCase(title)}
                </h3>
                <OKInformativeList>
                    <OKInformativeListItem
                        className="imposition-sidebar__item"
                        title={formatText('imposition-amount')}
                    >
                        <OneLineSymbolAmount
                            className="imposition-sidebar__amount"
                            amountObject={plainAmountObject}
                            language={language}
                            amountObjectFormatted={formatAmountObject(plainAmountObject, true)}
                        />
                    </OKInformativeListItem>
                    {fundsAmountElement}
                    <OKInformativeListItem
                        className="imposition-sidebar__item"
                        title={formatText('imposition-contractNumber')}
                        bodyText={contractNumber}
                    />
                    <ContactCenterRender>
                        {showImpositionNumber && (
                            <OKInformativeListItem
                                className="imposition-sidebar__item"
                                title={formatText('imposition-contractId')}
                                bodyText={impositionId}
                            />
                        )}
                    </ContactCenterRender>
                    {fundsElement}
                </OKInformativeList>
                <OKInformativeList>
                    <OKInformativeListItem
                        className="imposition-sidebar__item imposition-sidebar__item--border"
                        title={formatText('imposition-account')}
                        bodyText={account}
                    />
                </OKInformativeList>
                <h3 className="imposition-sidebar__interveners--title">
                    <FormattedText value="imposition-interveners" />
                </h3>
                {intervenerElement}
                <ContactCenterRender>
                    {this.buildCCImpositionActionLinks()}
                </ContactCenterRender>
            </OKSectionBox>
        );
    }
}

ImpositionSidebar.propTypes = {
    //We may need a Hiring Date prop, but this hasn't been confirmed yet.
    title: PropTypes.string.isRequired,
    amountObject: PropTypes.object.isRequired,
    contractNumber: PropTypes.string.isRequired,
    account: PropTypes.string.isRequired,
    immInterveners: PropTypes.object.isRequired,
    fund: PropTypes.object,
    isFetchingInterveners: PropTypes.bool,
    showImpositionCCActions: PropTypes.bool,
    showImpositionNumber: PropTypes.bool,
    immDeposits: PropTypes.object,
    impositionId: PropTypes.string,
    depositId: PropTypes.string
};

module.exports = ImpositionSidebar;
